/**
 * User management API service
 * Handles admin user CRUD and self-service profile operations
 */
import api, { getErrorMessage } from './api'
import type {
  User,
  UserCreate,
  UserUpdateAdmin,
  ProfileUpdate,
  UserListResponse,
  PasswordChangeRequest,
  ActivityEntry,
} from '../types'

// ===== Admin User Management =====

/** Columns the user-list endpoint will order by — mirrors `_USER_SORTS` in users.py. */
export type UserSortKey = 'created_at' | 'email' | 'full_name' | 'is_active' | 'last_login'

export async function listUsers(
  page = 1,
  limit = 50,
  sort?: UserSortKey,
  order?: 'asc' | 'desc',
): Promise<UserListResponse> {
  try {
    // `sort`/`order` are omitted when unset so the server's default (newest registered
    // first) is the single definition of it, rather than being restated here.
    const params: Record<string, string | number> = { page, limit }
    if (sort) params.sort = sort
    if (order) params.order = order
    const response = await api.get<UserListResponse>('/v1/users/', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getUser(id: number): Promise<User> {
  try {
    const response = await api.get<User>(`/v1/users/${id}`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function createUser(data: UserCreate): Promise<User> {
  try {
    const response = await api.post<User>('/v1/users/', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateUser(id: number, data: UserUpdateAdmin): Promise<User> {
  try {
    const response = await api.patch<User>(`/v1/users/${id}`, data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteUser(id: number): Promise<void> {
  try {
    await api.delete(`/v1/users/${id}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function resetPassword(
  id: number,
  new_password: string,
  force_password_change = true,
  reauthToken?: string | null,
): Promise<void> {
  try {
    await api.post(
      `/v1/users/${id}/reset-password`,
      { new_password, force_password_change },
      { headers: reauthToken ? { 'X-Admin-Reauth-Token': reauthToken } : undefined },
    )
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function unlockUser(id: number): Promise<void> {
  try {
    await api.post(`/v1/users/${id}/unlock`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function resendInvite(id: number): Promise<{ message: string }> {
  try {
    const resp = await api.post<{ message: string }>(`/v1/users/${id}/resend-invite`)
    return resp.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function terminateUserSessions(id: number, reauthToken?: string | null): Promise<void> {
  try {
    await api.delete(`/v1/users/${id}/sessions`, {
      headers: reauthToken ? { 'X-Admin-Reauth-Token': reauthToken } : undefined,
    })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function adminListSessions(userId: number): Promise<import('../types').UserSession[]> {
  try {
    const response = await api.get<import('../types').UserSession[]>(`/v1/users/${userId}/sessions`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function adminDeleteSession(
  userId: number,
  sessionId: number,
  reauthToken?: string | null,
): Promise<void> {
  try {
    await api.delete(`/v1/users/${userId}/sessions/${sessionId}`, {
      headers: reauthToken ? { 'X-Admin-Reauth-Token': reauthToken } : undefined,
    })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function adminReauth(
  currentPassword: string,
  mfaCode?: string,
): Promise<{ token: string; expires_in_seconds: number }> {
  try {
    const response = await api.post<{ token: string; expires_in_seconds: number }>(
      '/v1/users/admin-reauth',
      { current_password: currentPassword, mfa_code: mfaCode || undefined },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ===== Self-Service Profile =====

export async function getProfile(): Promise<User> {
  try {
    const response = await api.get<User>('/v1/users/me')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function changePassword(data: PasswordChangeRequest): Promise<void> {
  try {
    await api.patch('/v1/users/me/password', data)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Update own profile (name and phone only).
 * Never sends is_active or roles — backend returns 403 if those are present.
 */
export async function updateProfile(data: ProfileUpdate): Promise<User> {
  try {
    const response = await api.patch<User>('/v1/users/me', {
      full_name: data.full_name,
      phone: data.phone,
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getMyActivity(limit = 20): Promise<ActivityEntry[]> {
  try {
    const response = await api.get<ActivityEntry[]>('/v1/users/me/activity', { params: { limit } })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
